import React, { Component } from "react";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import { withRouter } from "../withRouter";
import {
  getEventByUrl,
  getEventColors,
  getVoterByLink,
  authVoter,
  updateVoterProfession,
} from "../api";
import Spinner from "react-bootstrap/Spinner";
import { withTheme } from "../withTheme";
import VoterNavBar from "./VoterNavBar.js";
import { BASE_URL } from "../config";
import LoadingButton from "./LoadingButton";

// Étape de choix de la profession (mode non-présentateur, projet à
// professions). Placée en garde-fou devant VoterRegister et VoterSession :
// on arrive ici avec { unique_str, unique_link, return_to_path,
// forward_state } et on repart vers return_to_path avec forward_state
// intact. Auto-skip si la profession est déjà choisie ou si le projet n'a
// pas de professions.
class VoterProfession extends Component {
  state = {
    event: null,
    colors: null,
    professions: [],
    selectedProfession: null,
    loading: true,
    error: false,
    error_message: "",
  };

  vh = window.innerHeight * 0.01;

  navigateBack = () => {
    const stateData = this.props.location.state || {};
    if (stateData.return_to_path) {
      this.props.navigate(stateData.return_to_path, {
        state: stateData.forward_state,
        replace: true,
      });
    } else {
      // Accès direct sans state : retour à la landing du votant.
      this.props.navigate(
        "/events/" + this.props.params.unique_str + "/voter",
        { replace: true }
      );
    }
  };

  async componentDidMount() {
    this.setVHValue();
    window.addEventListener("resize", this.setVHValue);

    const stateData = this.props.location.state || {};

    try {
      this.setState({ loading: true });
      const event = await getEventByUrl(this.props.params.unique_str);
      const colors = await getEventColors(event.id);

      if (!stateData.unique_link) {
        // Pas de lien : impossible d'identifier le votant, on repart.
        this.navigateBack();
        return;
      }

      const voterInfo = await getVoterByLink(event.id, stateData.unique_link);
      const professions = voterInfo.project_professions || [];

      // Auto-skip : profession déjà choisie (reconnexion) ou projet sans
      // professions.
      if (voterInfo.profession || professions.length === 0) {
        this.navigateBack();
        return;
      }

      this.setState({
        event,
        colors,
        professions,
        loading: false,
      });
    } catch (error) {
      let error_message = "";
      if (error.response && error.response.data) {
        error_message = error.response.data.detail;
      } else {
        error_message = error.message || "An error occurred";
      }
      this.setState({
        error: true,
        error_message,
        loading: false,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.setVHValue);
  }

  setVHValue = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  handleContinue = async () => {
    const stateData = this.props.location.state || {};

    try {
      // Auth par lien (non-présentateur) puis enregistrement de la
      // profession sur le votant.
      await authVoter(this.state.event.id, "", stateData.unique_link);
      await updateVoterProfession(
        this.state.event.id,
        this.state.selectedProfession
      );
      this.navigateBack();
    } catch (error) {
      let error_message = "";
      if (error.response && error.response.data) {
        error_message = error.response.data.detail;
      } else {
        error_message = error.message || "An error occurred";
      }
      this.setState({
        error: true,
        error_message,
      });
    }
  };

  render() {
    const { event, colors, professions, selectedProfession, loading, error, error_message } =
      this.state;

    const lang = event?.langage;
    const translations = {
      title: lang === "fr" ? "Vous êtes…" : "You are…",
      subtitle:
        lang === "fr"
          ? "Sélectionnez votre profession : les questions qui vous seront posées en dépendent."
          : "Select your profession: the questions you will be asked depend on it.",
      continue: lang === "fr" ? "Continuer" : "Continue",
      error: lang === "fr" ? "Erreur" : "Error",
    };

    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "calc(var(--vh, 1vh) * 100)",
            backgroundColor: colors?.background_color,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "calc(var(--vh, 1vh) * 100)",
            backgroundColor: colors?.background_color,
          }}
        >
          <VoterNavBar
            text={event?.event_title}
            backgroundColor={colors?.header_background_color}
            img={BASE_URL + "/events/" + event?.id + "/data/logo"}
          />
          <Container
            style={{ maxWidth: "800px" }}
            fluid
            className="px-3 pt-3 flex-grow-1 d-flex flex-column justify-content-center"
          >
            <Alert variant="danger">
              <Alert.Heading>{translations.error}</Alert.Heading>
              <p>{error_message}</p>
            </Alert>
          </Container>
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(var(--vh, 1vh) * 100)",
          position: "relative",
          paddingBottom: "54px",
          backgroundColor: colors?.background_color,
          overflowX: "hidden",
        }}
      >
        {/* Footer image */}
        {event && (
          <img
            src={BASE_URL + "/events/" + event.id + "/data/voter_footer"}
            style={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              height: "54px",
              objectFit: "fill",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
            }}
          />
        )}

        {/* Header */}
        {event && (
          <VoterNavBar
            text={event.event_title}
            backgroundColor={colors?.header_background_color}
            img={BASE_URL + "/events/" + event.id + "/data/logo"}
          />
        )}

        {/* Main content */}
        <Container
          style={{ maxWidth: "800px" }}
          fluid
          className="px-3 pt-3 flex-grow-1 d-flex flex-column"
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              className="p-3 mb-4"
              style={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                justifyContent: "center",
                borderRadius: "15px",
                backgroundColor: colors?.statement_background_color,
              }}
            >
              <span
                className="text-light mb-1"
                style={{
                  fontSize: "1.5em",
                }}
              >
                {translations.title}
              </span>
              <span className="text-light mb-3">{translations.subtitle}</span>
              <Form>
                {professions.map((profession) => (
                  <Form.Check
                    key={profession}
                    className="text-light mb-2"
                    type="radio"
                    id={`profession-${profession}`}
                    name="profession"
                    label={profession}
                    checked={selectedProfession === profession}
                    onChange={() =>
                      this.setState({ selectedProfession: profession })
                    }
                  />
                ))}
              </Form>
            </div>

            {/* Continue button */}
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <LoadingButton
                variant="dark"
                size="lg"
                disabled={!selectedProfession}
                onClick={this.handleContinue}
              >
                {translations.continue}
              </LoadingButton>
            </div>
          </div>

          {/* Legal notice */}
          {event && event.legal_notice && (
            <div
              className="text-center mb-2"
              style={{
                fontSize: "0.8rem",
                color: colors?.text_color || "#666",
              }}
            >
              {event.legal_notice}
            </div>
          )}
        </Container>
      </div>
    );
  }
}

export default withRouter(withTheme(VoterProfession));
